import React from 'react'
import cx from 'classnames'
import * as R from 'ramda'

import {
  Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'components/shared/Icon/Icon'
import { Tab } from './common/Tab/Tab'

import styles from './Tabs.module.scss'

type ChevronButtonProps = {
  icon?: unknown
  type?: 'left' | 'right'
  onClick?: (type?: 'left' | 'right') => void
  isDisabled?: boolean
  shouldRenderIcon?: boolean
}

const ChevronButton = ({
  icon,
  type,
  onClick,
  isDisabled,
  shouldRenderIcon = false,
}: ChevronButtonProps) => {
  const handleClick = React.useCallback(() => onClick?.(type), [type, onClick])

  return (
    <button
      className={styles.chevron}
      onClick={handleClick}
      disabled={isDisabled}
    >
      <span className={styles.content}>
        {shouldRenderIcon && (
          <Icon className={styles.icon} icon={icon} size={24} />
        )}
      </span>
    </button>
  )
}

type TabsProps = {
  children?: React.ReactNode
  className?: string
  isScrollable?: boolean
}

export const Tabs = ({ children, className, isScrollable }: TabsProps) => {
  const [
    { shouldRenderLeftChevron, shouldRenderRightChevron },
    setScrollState,
  ] = React.useState({
    shouldRenderLeftChevron: false,
    shouldRenderRightChevron: true,
  })

  const ref = React.useRef<HTMLDivElement>(null)

  const handleScrollCapture = React.useCallback(() => {
    setScrollState({
      shouldRenderLeftChevron: R.gt(ref.current?.scrollLeft, 0),
      shouldRenderRightChevron: R.lt(
        (ref.current?.scrollLeft ?? 0) + (ref.current?.offsetWidth ?? 0),
        ref.current?.scrollWidth,
      ),
    })
  }, [ref, setScrollState])

  const handleChevronClick = React.useCallback(
    type => {
      const mapSideToValue = {
        left: 0,
        right: ref?.current?.scrollWidth,
      }

      ref.current?.scroll({
        top: 0,
        left: mapSideToValue[type],
        behavior: 'smooth',
      })
    },
    [ref],
  )

  return (
    <div
      className={cx(styles.tabs, className)}
      data-testid="tabs"
      onScrollCapture={handleScrollCapture}
    >
      {isScrollable && (
        <ChevronButton
          icon={ChevronLeftIcon}
          type="left"
          isDisabled={R.not(shouldRenderLeftChevron)}
          shouldRenderIcon={shouldRenderLeftChevron}
          {...(shouldRenderLeftChevron && {
            onClick: handleChevronClick,
          })}
        />
      )}
      <div className={styles.content} ref={ref}>
        {children}
      </div>
      {isScrollable && (
        <ChevronButton
          icon={ChevronRightIcon}
          type="right"
          isDisabled={R.not(shouldRenderRightChevron)}
          shouldRenderIcon={shouldRenderRightChevron}
          {...(shouldRenderRightChevron && {
            onClick: handleChevronClick,
          })}
        />
      )}
    </div>
  )
}

Tabs.Tab = Tab
